// src/features/chat/hooks/useDelayedTrue.js

import { useEffect, useState } from 'react';

/**
 * Delays a boolean value becoming true by the specified delay.
 * Immediately returns false when the value becomes false.
 *
 * Typical use case: prevent loading spinners from flickering
 * on very short loading states (e.g., <200ms).
 *
 * @param {boolean} value - The boolean value to delay when true.
 * @param {number} [delayMs=200] - Delay in milliseconds before returning true.
 * @returns {boolean} - Delayed boolean value.
 */
export default function useDelayedTrue(value, delayMs = 200) {
  const [delayed, setDelayed] = useState(false);

  useEffect(() => {
    // If value is false: immediately clear and cancel any pending timer.
    if (!value) {
      setDelayed(false);
      return undefined;
    }

    // If value is true: start a timer to set delayed to true.
    const timeoutId = setTimeout(() => {
      setDelayed(true);
    }, delayMs);

    // Cleanup: if value changes before delay expires, cancel this timer.
    return () => clearTimeout(timeoutId);
  }, [value, delayMs]);

  return delayed;
}
